import {IItemPreguntaCargaDTO, IItemPreguntaDTO, ItemPreguntaCargaDTO, ItemPreguntaDTO} from "./ItemPregunta.DTO";
import {
    MultipleSubtipoEnum,
    NPSSubtipoEnum,
    obtenerIdPreguntaTipo,
    PreguntasTipoEnum,
    SingleSubtipoEnum,
    Subtipo
} from "@shared/enums";
import {esNumero, Nilable, Nullable} from "@shared/utils";

interface IPreguntaBaseDTO {
    combo: number;
    image_url: Nilable<string>;
    item_selection_maximum: number;
    item_selection_minimum: number;
    maximum: Nilable<number>;
    step: Nilable<number>;
    text: string;
    name?: Nilable<string>;
    type: string | number;
    type_config: number;
    url: Nilable<string>;
    minimum_time: number;
    minimum_categories_item: number;
    maximum_categories_item: number;
    conditions: Nilable<string>;
    reference_text: Nilable<string>;
    next_question: Nilable<number>;
    minimum: Nilable<number>;
    random: Nilable<boolean>;
    optional: boolean;
    minimum_answers: Nilable<number>;
    customer_id: Nilable<string>;
    payload: any;
    has_image: boolean;
}

export interface IPreguntaDTO extends IPreguntaBaseDTO {
    id: number;
    questions: Nilable<IPreguntaDTO[]>;
    items: IItemPreguntaDTO[];
    parent: Nilable<number>;
    block_id: Nilable<number>;
}

export interface IPreguntaCargaDTO extends IPreguntaBaseDTO {
    id: string;
    questions: Nilable<IPreguntaCargaDTO[]>;
    items: IItemPreguntaCargaDTO[];
    parent: Nilable<string>;
    block_id: Nilable<string>;
}

class PreguntaBaseDTO {
    public encuesta = 0;
    public imagenUrl: Nilable<string>;
    public maximoItemsSeleccionados = 0;
    public minimoItemsSeleccionados = 0;
    public maximo: Nilable<number>;
    public paso: Nilable<number>;
    public texto = "";
    public subtipo: Subtipo = SingleSubtipoEnum.REGULAR;
    public url = "";
    public tiempoMinimo = 0;
    public minimoCategoriasItem = 0;
    public maximoCategoriasItem = 0;
    public condiciones = "";
    public textoReferencia = "";
    public siguientePregunta: Nilable<number>;
    public minimo: Nilable<number>;
    public aleatorio: Nilable<boolean>;
    public opcional = false;
    public minimoRespuestas = 0;
    public idRelativo = "";
    public idCliente = "";
    public payload: any = {};
    public tieneImagen = false;

    private _tipo = PreguntasTipoEnum.SINGLE_CHOICE;

    constructor(json: Nilable<Partial<IPreguntaDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IPreguntaBaseDTO>) {
        if (json.combo != null) {
            this.encuesta = json.combo;
        }
        if (json.image_url != null) {
            this.imagenUrl = json.image_url;
        }
        if (json.item_selection_maximum != null) {
            this.maximoItemsSeleccionados = json.item_selection_maximum;
        }
        if (json.item_selection_minimum != null) {
            this.minimoItemsSeleccionados = json.item_selection_minimum;
        }
        if (json.maximum != null) {
            this.maximo = json.maximum;
        }
        if (json.step != null) {
            this.paso = json.step;
        }
        if (json.text != null) {
            this.texto = json.text;
        }
        if (json.type != null) {
            if (esNumero(json.type)) {
                this.tipo = json.type as PreguntasTipoEnum;
            } else {
                this.tipo = PreguntasTipoEnum[json.type];
            }
        }
        if (json.type_config != null) {
            this.subtipo = json.type_config;
        }
        if (json.url != null) {
            this.url = json.url;
        }
        if (json.minimum_time != null) {
            this.tiempoMinimo = json.minimum_time;
        }
        if (json.minimum_categories_item != null) {
            this.minimoCategoriasItem = json.minimum_categories_item;
        }
        if (json.maximum_categories_item != null) {
            this.maximoCategoriasItem = json.maximum_categories_item;
        }
        if (json.conditions != null) {
            this.condiciones = json.conditions;
        }
        if (json.reference_text != null) {
            this.textoReferencia = json.reference_text;
        }
        if (json.next_question != null) {
            this.siguientePregunta = json.next_question;
        }
        if (json.minimum != null) {
            this.minimo = json.minimum;
        }
        if (json.random != null) {
            this.aleatorio = json.random;
        }
        if (json.optional != null) {
            this.opcional = json.optional;
        }
        if (json.minimum_answers != null) {
            this.minimoRespuestas = json.minimum_answers;
        }
        if (json.customer_id != null) {
            this.idCliente = json.customer_id;
        }
        if (this.payload != null) {
            this.payload = json.payload;
        }
        if (json.name != null) {
            this.texto = json.name;
        }
        if (json.has_image != null) {
            this.tieneImagen = json.has_image;
        }
    }

    public toJSON(): IPreguntaBaseDTO {
        return {
            combo: this.encuesta,
            image_url: this.imagenUrl,
            item_selection_maximum: this.maximoItemsSeleccionados,
            item_selection_minimum: this.minimoItemsSeleccionados,
            maximum: this.maximo,
            step: this.paso,
            text: this.texto,
            type: obtenerIdPreguntaTipo(this.tipo),
            type_config: this.subtipo,
            url: this.url,
            minimum_time: this.tiempoMinimo,
            minimum_categories_item: this.minimoCategoriasItem,
            maximum_categories_item: this.maximoCategoriasItem,
            conditions: this.condiciones,
            reference_text: this.textoReferencia,
            next_question: this.siguientePregunta,
            minimum: this.minimo,
            random: this.aleatorio,
            optional: this.opcional,
            minimum_answers: this.minimoRespuestas,
            customer_id: this.idCliente,
            payload: this.payload ?? {},
            has_image: this.tieneImagen
        };
    }

    public get tipo(): PreguntasTipoEnum {
        return this._tipo;
    }

    public set tipo(tipo: PreguntasTipoEnum) {
        this._tipo = tipo;
        if (tipo === PreguntasTipoEnum.MULTIPLE_CHOICE) {
            this.subtipo = MultipleSubtipoEnum.REGULAR;
        }
        if (tipo === PreguntasTipoEnum.NET_PROMOTER_SCORE) {
            this.subtipo = NPSSubtipoEnum.SLIDER;
        }
    }
}

export class PreguntaDTO extends PreguntaBaseDTO {
    public id = 0;
    public items: ItemPreguntaDTO[] = [];
    public preguntas: Nullable<PreguntaDTO[]>;
    public padre: Nilable<number>;
    public idBloque: Nilable<number>;

    constructor(json: Nilable<Partial<IPreguntaDTO>> = null) {
        super();
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IPreguntaDTO>) {
        super.cargarDTO(json);
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.items != null) {
            this.items = json.items.map(item => new ItemPreguntaDTO(item));
        }
        if (json.parent != null) {
            this.padre = json.parent;
        }
        if (json.questions != null) {
            this.preguntas = json.questions.map(pregunta => new PreguntaDTO(pregunta));
        }
        if (json.block_id != null) {
            this.idBloque = json.block_id;
        }
    }

    public toJSON(): IPreguntaDTO {
        return {
            ...super.toJSON(),
            id: this.id,
            items: this.items.map(item => item.toJSON()),
            questions: this.preguntas?.map(pregunta => pregunta.toJSON()) ?? null,
            parent: this.padre,
            block_id: this.idBloque
        };
    }
}

export class PreguntaCargaDTO extends PreguntaBaseDTO {
    public id = "";
    public items: ItemPreguntaCargaDTO[] = [];
    public preguntas: Nilable<PreguntaCargaDTO[]>;
    public padre: string;
    public idBloque: Nilable<string>;

    constructor(json: Nilable<Partial<IPreguntaCargaDTO>> = null) {
        super();
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IPreguntaCargaDTO>) {
        super.cargarDTO(json);
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.items != null) {
            this.items = json.items.map(item => new ItemPreguntaCargaDTO(item));
        }
        if (json.questions != null) {
            this.preguntas = json.questions.map(pregunta => new PreguntaCargaDTO(pregunta));
        }
        if (json.block_id != null) {
            this.idBloque = json.block_id;
        }
        if (json.parent != null) {
            this.padre = json.parent;
        }
    }

    public toJSON(): IPreguntaCargaDTO {
        return {
            ...super.toJSON(),
            id: this.id,
            items: this.items.map(item => item.toJSON()),
            questions: this.preguntas?.map(pregunta => pregunta.toJSON()) ?? null,
            parent: this.padre,
            block_id: this.idBloque
        };
    }
}
